import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { centralService } from '../../../services/centralService';
import Input from '../../ui/Input';
import Select from '../../ui/Select';
import Button from '../../ui/Button';
import { 
  CENTRAL_PROJECT_TYPES,
  DEFAULT_UC_DATA
} from '../../../constants/centralConstants';
import { validateUCForm } from '../../../utils/centralValidation';
import { financialYearUtils, dateUtils } from '../../../utils/helpers';

const NecUcForm = () => {
  const { user } = useAuth();
  const { isMobile, getResponsiveValue } = useTheme();
  
  const [formData, setFormData] = useState({
    ...DEFAULT_UC_DATA,
    projectType: CENTRAL_PROJECT_TYPES.NEC
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [projectOptions, setProjectOptions] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [existingUCs, setExistingUCs] = useState([]);
  const [loadingUCs, setLoadingUCs] = useState(false);

  // Responsive configurations
  const titleSize = getResponsiveValue({
    mobile: 'text-xl',
    desktop: 'text-2xl'
  });

  const gridCols2 = getResponsiveValue({
    mobile: 'grid-cols-1',
    desktop: 'grid-cols-2'
  });

  const gridCols3 = getResponsiveValue({
    mobile: 'grid-cols-1',
    tablet: 'grid-cols-2',
    desktop: 'grid-cols-3'
  });

  const gridGap = getResponsiveValue({
    mobile: 'gap-4',
    desktop: 'gap-6'
  });

  // Load NEC projects for dropdown
  useEffect(() => {
    loadNecProjects();
  }, []);

  // Set initial financial year
  useEffect(() => {
    const currentFY = financialYearUtils.getCurrentFinancialYear();
    setFormData(prev => ({
      ...prev,
      financialYear: currentFY
    }));
  }, []);

  const loadNecProjects = async () => {
    try {
      setLoadingProjects(true);
      const result = await centralService.getCentralProjectsForDropdown(CENTRAL_PROJECT_TYPES.NEC);
      
      if (result.success) {
        setProjectOptions(result.data);
      }
    } catch (error) {
      console.error('Error loading NEC projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadExistingUCs = async (projectId) => {
    try {
      setLoadingUCs(true);
      const result = await centralService.getUCsByProjectId(projectId);
      
      if (result.success) {
        setExistingUCs(result.data);
      }
    } catch (error) {
      console.error('Error loading existing UCs:', error);
      setExistingUCs([]);
    } finally {
      setLoadingUCs(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    if (success) setSuccess(false);
  };

  const handleProjectSelect = async (projectId) => {
    const selectedProject = projectOptions.find(p => p.value === projectId);
    
    if (selectedProject) {
      try {
        // Get project details with central share
        const projectResult = await centralService.getProjectWithCentralShare(projectId);
        
        if (projectResult.success) {
          const projectData = projectResult.data;
          
          setFormData(prev => ({
            ...prev,
            projectId: projectId,
            projectName: projectData.projectName,
            centralShare: projectData.centralShare || 0
          }));
          
          // Load existing UCs for this project
          await loadExistingUCs(projectId);
        }
      } catch (error) {
        console.error('Error loading project details:', error);
      }
    }
    
    // Clear project error
    if (errors.projectId) {
      setErrors(prev => ({
        ...prev,
        projectId: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateUCForm(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }
    
    try {
      setLoading(true);
      setErrors({});
      
      const result = await centralService.submitUC(formData, user.uid);
      
      if (result.success) {
        setSuccess(true);
        setSubmitMessage('NEC UC submitted successfully!');
        
        // Reload existing UCs to show the new one
        if (formData.projectId) {
          await loadExistingUCs(formData.projectId);
        }
        
        // Reset form but keep project selection
        const currentProjectId = formData.projectId;
        const currentProjectName = formData.projectName;
        const currentCentralShare = formData.centralShare;
        
        setFormData({
          ...DEFAULT_UC_DATA,
          projectType: CENTRAL_PROJECT_TYPES.NEC,
          financialYear: financialYearUtils.getCurrentFinancialYear(),
          projectId: currentProjectId,
          projectName: currentProjectName,
          centralShare: currentCentralShare
        });
        
        setTimeout(() => {
          setSuccess(false);
          setSubmitMessage('');
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting NEC UC:', error);
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  const financialYearOptions = financialYearUtils.getFinancialYearOptions();

  return (
    <div className="space-y-4">
      {/* Success Message */}
      {success && (
        <div className="bg-success-subtle border border-border-primary rounded-xl p-3">
          <div className="flex items-center">
            <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-success font-medium text-sm">{submitMessage}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className={`${titleSize} font-bold text-text-primary mb-1`}>NEC Utilization Certificate</h1>
        <p className="text-text-secondary text-sm">
          Submit utilization certificate details for NEC projects
        </p>
      </div>

      {/* Main Content Grid */}
      <div className={`grid ${gridCols2} ${gridGap}`}>
        {/* UC Form */}
        <div>
          <div>
            <h2 className="text-lg font-semibold text-text-primary mb-4 border-b border-border-primary pb-2">
              UC Form Details
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* General Error */}
              {errors.general && (
                <div className="bg-error-subtle border border-border-error rounded-xl p-3">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-error text-sm">{errors.general}</span>
                  </div>
                </div>
              )}

              {/* Financial Year and Project */}
              <div className="grid grid-cols-1 gap-4">
                <Select
                  label="Financial Year"
                  value={formData.financialYear}
                  onChange={(e) => handleInputChange('financialYear', e.target.value)}
                  options={financialYearOptions}
                  error={errors.financialYear}
                  required
                />

                <Select
                  label="Name of Project"
                  value={formData.projectId}
                  onChange={(e) => handleProjectSelect(e.target.value)}
                  options={projectOptions}
                  error={errors.projectId}
                  required
                  placeholder={loadingProjects ? "Loading projects..." : "Select NEC Project"}
                  disabled={loadingProjects}
                />
              </div>

              {/* NEC Share - Auto-populated and read-only */}
              {formData.projectId && (
                <div>
                  <Input
                    label="NEC Share (in lakh)"
                    type="text"
                    value={formData.centralShare ? `₹${formData.centralShare.toLocaleString('en-IN')}` : '₹0.00'}
                    readOnly
                    className="bg-gray-50"
                    icon={
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    }
                  />
                </div>
              )}

              {/* UC Details */}
              <div className="space-y-4">
                <Input
                  label="UC Number"
                  type="text"
                  value={formData.ucNumber}
                  onChange={(e) => handleInputChange('ucNumber', e.target.value)}
                  placeholder="Enter UC number"
                  error={errors.ucNumber}
                  required
                />

                <Input
                  label="UC Certificate Date"
                  type="date"
                  value={formData.ucCertificateDate}
                  onChange={(e) => handleInputChange('ucCertificateDate', e.target.value)}
                  error={errors.ucCertificateDate}
                  required
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  }
                />

                <Input
                  label="UC Furnished Amount (in lakh)"
                  type="number"
                  value={formData.ucFurnished}
                  onChange={(e) => handleInputChange('ucFurnished', e.target.value)}
                  placeholder="Enter UC furnished amount"
                  error={errors.ucFurnished}
                  required
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  }
                />

                <Input
                  label="UC Due Amount (in lakh)"
                  type="number"
                  value={formData.ucDue}
                  onChange={(e) => handleInputChange('ucDue', e.target.value)}
                  placeholder="Enter UC due amount"
                  error={errors.ucDue}
                  required
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  }
                />
              </div>

              {/* Submit Button */}
              <div className="pt-4 border-t border-border-primary">
                <Button
                  type="submit"
                  loading={loading}
                  disabled={loading || loadingProjects || !formData.projectId}
                  size="md"
                  className="w-full"
                >
                  {loading ? 'Submitting...' : 'Submit UC'}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Existing UCs Display */}
        <div>
          <div>
            <h2 className="text-lg font-semibold text-text-primary mb-4 border-b border-border-primary pb-2">
              Previous UCs for Selected Project
            </h2>
            
            {!formData.projectId ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-text-secondary text-sm">Select a project to view previous UCs</p>
              </div>
            ) : loadingUCs ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3"></div>
                <p className="text-text-secondary text-sm">Loading UCs...</p>
              </div>
            ) : existingUCs.length === 0 ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-text-secondary text-sm">No previous UCs found for this project</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {existingUCs.map((uc, index) => (
                  <div key={uc.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-text-primary text-sm">UC #{index + 1}</h3>
                      <span className="text-xs text-text-tertiary">
                        {dateUtils.formatDate(uc.createdAt)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-2 text-xs">
                      <div className="flex justify-between">
                        <span className="text-text-secondary">UC Number:</span>
                        <span className="text-text-primary font-medium">{uc.ucNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">UC Date:</span>
                        <span className="text-text-primary font-medium">
                          {uc.ucCertificateDate ? dateUtils.formatDate(uc.ucCertificateDate) : 'Not specified'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">UC Furnished:</span>
                        <span className="text-text-primary">₹{uc.ucFurnished?.toLocaleString('en-IN') || '0'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">UC Due:</span>
                        <span className="text-text-primary">₹{uc.ucDue?.toLocaleString('en-IN') || '0'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Financial Year:</span>
                        <span className="text-text-primary">FY {uc.financialYear}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NecUcForm;